import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Chip,
    Alert,
    CircularProgress,
    IconButton,
    MenuItem,
    Autocomplete,
    ToggleButtonGroup,
    ToggleButton,
    Avatar,
} from '@mui/material';
import { Close, Category, AutoAwesome, Inventory2, Layers } from '@mui/icons-material';
import { WarehouseAPI } from '../../services/api';

interface CreateBundleCategoryProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

interface PlytixSkuOption {
    sku: string;
    name: string;
    category: string | null;
    subCategory: string | null;
    brand: string | null;
    handle: string | null;
    tags: string[];
    costPrice: number | null;
    basePrice: number | null;
    maxRetailPrice: number | null;
    plytixId: string | null;
    images: string[];
}

const CreateBundleCategory: React.FC<CreateBundleCategoryProps> = ({ open, onClose, onSuccess }) => {
    // "Simple" = one real product picked from Plytix; "Bundle" = multiple components
    // combined. Drives saleType directly — replaces what used to be a separate, easy-to-
    // ignore Sale Type dropdown that only 6 of 1,235 active bundles ever had set correctly.
    const [mode, setMode] = useState<'Simple' | 'Bundle'>('Bundle');

    // Live SKU picker — replaces free-text child-SKU entry, which accepted any typo or
    // nonexistent SKU with no validation at all.
    const [skuQuery, setSkuQuery] = useState('');
    const [skuOptions, setSkuOptions] = useState<PlytixSkuOption[]>([]);
    const [skuSearchLoading, setSkuSearchLoading] = useState(false);
    const [selectedComponents, setSelectedComponents] = useState<PlytixSkuOption[]>([]);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [parentSKU, setParentSKU] = useState('');
    const [suggestingName, setSuggestingName] = useState(false);
    const [imageURL, setImageURL] = useState('');
    const [categoryCode, setCategoryCode] = useState('');
    const [name, setName] = useState('');
    const [type, setType] = useState('BUNDLE');
    const [maxRetailPrice, setMaxRetailPrice] = useState('');
    const [basePrice, setBasePrice] = useState('');
    const [brand, setBrand] = useState('');
    const [category, setCategory] = useState('');
    const [subCategory, setSubCategory] = useState('');
    const [handle, setHandle] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [plytixId, setPlytixId] = useState('');
    const [costPrice, setCostPrice] = useState('');
    const [gstTaxTypeCode, setGstTaxTypeCode] = useState('3');
    const [saleTag, setSaleTag] = useState('Live');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Brand: existing distinct values from this tool's own data — pick-only (no freeSolo)
    // so future bundles can't introduce new casing variants like the SALTY/Salty/salty
    // mess that's being cleaned up separately.
    const [brandOptions, setBrandOptions] = useState<string[]>([]);
    // Category: live Plytix-derived options — the SAME source as Component View's
    // "Product Category" filter (getComponentCategoryOptions), not this tool's own
    // (previously messy/coarse) category values. Also pick-only.
    const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
    const [tagOptions, setTagOptions] = useState<string[]>([]);
    // Category Code: Unicommerce is the source of truth for this field — pick-only,
    // so it can't be mistyped the way free text always eventually is.
    const [categoryCodeOptions, setCategoryCodeOptions] = useState<Array<{ categoryCode: string; categoryName: string | null }>>([]);

    useEffect(() => {
        if (!open) return;
        WarehouseAPI.getBundleCategoryFilterValues().then((vals) => {
            setBrandOptions(vals.brands || []);
            setTagOptions(vals.tags || []);
        }).catch(() => {});
        WarehouseAPI.getComponentCategoryOptions().then(setCategoryOptions).catch(() => {});
        WarehouseAPI.getUnicommerceCategoryCodes().then(setCategoryCodeOptions).catch(() => {});
    }, [open]);

    // Debounced live search against Redshift's plytix_catalogue (prefix-match only).
    useEffect(() => {
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        if (!skuQuery.trim()) {
            setSkuOptions([]);
            return;
        }
        searchDebounceRef.current = setTimeout(async () => {
            setSkuSearchLoading(true);
            try {
                const results = await WarehouseAPI.searchPlytixSkus(skuQuery.trim());
                setSkuOptions(results);
            } finally {
                setSkuSearchLoading(false);
            }
        }, 500);
        return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
    }, [skuQuery]);

    // Auto-fills from the picked product whenever exactly ONE component is selected —
    // regardless of Simple/Bundle mode. Simple vs Bundle is purely the saleType business
    // classification, not a component-count rule: real data has Simple bundles with many
    // child SKUs (e.g. an Advent calendar box with 9 components, still classified Simple).
    // With 2+ components picked, nothing gets auto-filled — no single Plytix record
    // represents a multi-component combo's price/category either way.
    const applyAutoFillFromSingle = (comp: PlytixSkuOption) => {
        setName(comp.name);
        if (comp.brand) {
            setBrand(comp.brand);
            // Same reasoning as categoryOptions below — the picked SKU's live brand may not
            // yet be in the cached options list, so add it to keep the strict dropdown consistent.
            setBrandOptions(prev => (prev.includes(comp.brand as string) ? prev : [...prev, comp.brand as string]));
        }
        if (comp.category) {
            setCategory(comp.category);
            // The picked SKU's live category may not yet be in the cached options list
            // (built from already-used bundle components, not the full Plytix catalogue)
            // — add it so the now-strict Category dropdown stays consistent, not broken.
            setCategoryOptions(prev => (prev.includes(comp.category as string) ? prev : [...prev, comp.category as string]));
        }
        if (comp.subCategory) setSubCategory(comp.subCategory);
        if (comp.handle) setHandle(comp.handle);
        if (comp.tags.length > 0) setTags(comp.tags);
        if (comp.costPrice != null) setCostPrice(String(comp.costPrice));
        if (comp.basePrice != null) setBasePrice(String(comp.basePrice));
        if (comp.maxRetailPrice != null) setMaxRetailPrice(String(comp.maxRetailPrice));
        if (comp.plytixId) setPlytixId(comp.plytixId);
        if (comp.images.length > 0) setImageURL(comp.images[0]);
    };

    const handleModeChange = (_: React.MouseEvent<HTMLElement>, newMode: 'Simple' | 'Bundle' | null) => {
        if (!newMode || newMode === mode) return;
        setMode(newMode);
        // Keep the legacy `type` field (Bundle Details section) in sync with the mode
        // toggle — left unsynced, this silently produced saleType/type contradictions
        // (e.g. saleType: "Bundle" with type: "SIMPLE") whenever someone didn't also
        // remember to change the separate Type dropdown by hand.
        setType(newMode === 'Bundle' ? 'BUNDLE' : 'SIMPLE');
        setSelectedComponents([]);
        setSkuOptions([]);
        setSkuQuery('');
    };

    const handleComponentsChange = (_: React.SyntheticEvent, value: PlytixSkuOption[] | null) => {
        const next = value || [];
        setSelectedComponents(next);
        if (next.length === 1) applyAutoFillFromSingle(next[0]);
    };

    const handleSuggestName = async () => {
        if (selectedComponents.length === 0) {
            setError('Pick at least one SKU before suggesting a name');
            return;
        }
        setSuggestingName(true);
        setError(null);
        try {
            const suggestion = await WarehouseAPI.suggestParentName(
                mode,
                selectedComponents.map(c => ({ sku: c.sku, name: c.name, category: c.category }))
            );
            if (suggestion) {
                setParentSKU(suggestion.parentSKU);
                setName(suggestion.name);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to get AI name suggestion');
        } finally {
            setSuggestingName(false);
        }
    };

    const handleSubmit = async () => {
        if (!parentSKU.trim()) {
            setError('Parent SKU is required');
            return;
        }
        if (selectedComponents.length === 0) {
            setError('At least one component SKU is required');
            return;
        }
        if (parentSKU.trim().toUpperCase() === name.trim().toUpperCase()) {
            setError('Bundle Name cannot be the same as Parent SKU — enter a distinct display name');
            return;
        }
        if (!categoryCode.trim()) {
            setError('Category Code is required');
            return;
        }
        if (!name.trim()) {
            setError('Bundle Name is required');
            return;
        }
        if (!type.trim()) {
            setError('Type is required');
            return;
        }
        if (!maxRetailPrice || parseFloat(maxRetailPrice) <= 0) {
            setError('Max Retail Price is required and must be greater than 0');
            return;
        }
        if (!basePrice || parseFloat(basePrice) <= 0) {
            setError('Base Price is required and must be greater than 0');
            return;
        }
        if (!brand.trim()) {
            setError('Brand is required');
            return;
        }
        if (!category.trim()) {
            setError('Category is required');
            return;
        }
        if (tags.length === 0) {
            setError('At least one tag is required');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await WarehouseAPI.createBundleCategory({
                parentSKU: parentSKU.trim(),
                childs: selectedComponents.map(c => c.sku),
                imageURL: imageURL.trim() || undefined,
                categoryCode: categoryCode.trim(),
                name: name.trim(),
                type: type.trim(),
                maxRetailPrice: parseFloat(maxRetailPrice),
                basePrice: parseFloat(basePrice),
                brand: brand.trim(),
                category: category.trim(),
                subCategory: subCategory.trim() || undefined,
                handle: handle.trim() || undefined,
                tags,
                plytix_id: plytixId.trim() || undefined,
                costPrice: costPrice ? parseFloat(costPrice) : undefined,
                gstTaxTypeCode: gstTaxTypeCode.trim() || undefined,
                saleType: mode,
                saleTag,
            });
            handleReset();
            onSuccess();
        } catch (err: any) {
            setError(err.message || 'Failed to create bundle category');
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setMode('Bundle');
        setSkuQuery('');
        setSkuOptions([]);
        setSelectedComponents([]);
        setParentSKU('');
        setImageURL('');
        setCategoryCode('');
        setName('');
        setType('BUNDLE');
        setMaxRetailPrice('');
        setBasePrice('');
        setBrand('');
        setCategory('');
        setSubCategory('');
        setHandle('');
        setTags([]);
        setPlytixId('');
        setCostPrice('');
        setGstTaxTypeCode('3');
        setSaleTag('Live');
        setError(null);
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    // Auto-fill only applies when exactly one component is picked — see
    // applyAutoFillFromSingle for why this isn't tied to Simple/Bundle mode.
    const isSingleComponent = selectedComponents.length === 1;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Category color="primary" />
                Create New Bundle Category
                <IconButton
                    onClick={handleClose}
                    sx={{ ml: 'auto' }}
                    size="small"
                >
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {error && (
                        <Alert severity="error" onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Type *
                        </Typography>
                        <ToggleButtonGroup
                            value={mode}
                            exclusive
                            onChange={handleModeChange}
                            disabled={loading}
                            fullWidth
                            sx={{
                                gap: 1.5,
                                '& .MuiToggleButtonGroup-grouped': {
                                    border: '2px solid #ECECE4',
                                    borderRadius: '8px !important',
                                    mx: 0,
                                },
                                '& .MuiToggleButton-root': {
                                    py: 1.75,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.9rem',
                                    color: 'text.secondary',
                                    gap: 1,
                                    transition: 'all 0.15s ease',
                                    '&:hover': {
                                        borderColor: '#dc5829',
                                        bgcolor: 'rgba(220, 88, 41, 0.04)',
                                    },
                                    '&.Mui-selected': {
                                        background: 'linear-gradient(135deg, #DC5829 0%, #B8451E 100%)',
                                        color: '#fff',
                                        borderColor: 'transparent',
                                        boxShadow: '0 2px 8px rgba(220, 88, 41, 0.35)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #b8451e 0%, #DC5829 100%)',
                                        },
                                    },
                                },
                            }}
                        >
                            <ToggleButton value="Simple">
                                <Inventory2 fontSize="small" />
                                Simple — one real product
                            </ToggleButton>
                            <ToggleButton value="Bundle">
                                <Layers fontSize="small" />
                                Bundle — multiple components
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Component SKUs *
                        </Typography>
                        <Autocomplete
                            multiple
                            options={skuOptions}
                            value={selectedComponents}
                            onChange={handleComponentsChange}
                            onInputChange={(_, val) => setSkuQuery(val)}
                            getOptionLabel={(opt) => `${opt.sku} — ${opt.name}`}
                            isOptionEqualToValue={(opt, val) => opt.sku === val.sku}
                            loading={skuSearchLoading}
                            filterOptions={(x) => x}
                            disabled={loading}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.sku} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Avatar src={option.images[0]} variant="rounded" sx={{ width: 32, height: 32, bgcolor: 'grey.200' }}>
                                        {option.sku[0]}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{option.sku}</Typography>
                                        <Typography variant="caption" color="text.secondary">{option.name}</Typography>
                                    </Box>
                                </Box>
                            )}
                            renderTags={(value, getTagProps) =>
                                value.map((opt, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={opt.sku}
                                        avatar={<Avatar src={opt.images[0]}>{opt.sku[0]}</Avatar>}
                                        label={opt.sku}
                                        size="small"
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search by SKU and add each component..."
                                    helperText="Searches Redshift's live product catalogue by SKU prefix — pick real SKUs instead of typing them by hand"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {skuSearchLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                        {selectedComponents.length === 0 && (
                            <Typography variant="caption" color="text.secondary">
                                Add at least one component SKU
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <TextField
                            fullWidth
                            label="Parent SKU"
                            placeholder="e.g., PINKBOX"
                            value={parentSKU}
                            onChange={(e) => setParentSKU(e.target.value.toUpperCase())}
                            helperText="This will be the base name for all versions (e.g., PINKBOX-1, PINKBOX-2)"
                            disabled={loading}
                        />
                        <Button
                            variant="outlined"
                            startIcon={suggestingName ? <CircularProgress size={16} /> : <AutoAwesome />}
                            onClick={handleSuggestName}
                            disabled={loading || suggestingName || selectedComponents.length === 0}
                            sx={{ whiteSpace: 'nowrap', mt: 0.5 }}
                        >
                            Suggest
                        </Button>
                    </Box>

                    {/* Bundle Details Section */}
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, fontWeight: 600 }}>
                        Bundle Details
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Autocomplete
                            fullWidth
                            options={categoryCodeOptions}
                            getOptionLabel={(opt) => opt.categoryName ? `${opt.categoryCode} — ${opt.categoryName}` : opt.categoryCode}
                            isOptionEqualToValue={(opt, val) => opt.categoryCode === val.categoryCode}
                            value={categoryCodeOptions.find(o => o.categoryCode === categoryCode) || null}
                            onChange={(_, val) => setCategoryCode(val?.categoryCode || '')}
                            disabled={loading}
                            renderInput={(params) => (
                                <TextField {...params} label="Category Code" placeholder="Pick a category code" required helperText="Sourced from Unicommerce" />
                            )}
                        />
                        <TextField
                            fullWidth
                            label="Bundle Name"
                            placeholder="e.g., Diwali Gift Box"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={loading}
                            required
                            error={!!parentSKU.trim() && !!name.trim() && parentSKU.trim().toUpperCase() === name.trim().toUpperCase()}
                            helperText={
                                !!parentSKU.trim() && !!name.trim() && parentSKU.trim().toUpperCase() === name.trim().toUpperCase()
                                    ? 'Cannot be the same as Parent SKU'
                                    : (isSingleComponent ? 'Auto-filled from the picked product — editable' : undefined)
                            }
                        />
                    </Box>

                    <TextField
                        fullWidth
                        select
                        label="Type"
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        disabled={loading}
                        required
                    >
                        <MenuItem value="SIMPLE">SIMPLE</MenuItem>
                        <MenuItem value="BUNDLE">BUNDLE</MenuItem>
                    </TextField>

                    <TextField
                        fullWidth
                        select
                        label="Sale Tag *"
                        value={saleTag}
                        onChange={(e) => setSaleTag(e.target.value)}
                        disabled={loading}
                        required
                    >
                        <MenuItem value="Live">Live</MenuItem>
                        <MenuItem value="Sunset">Sunset</MenuItem>
                    </TextField>

                    {/* Additional Fields Section */}
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, fontWeight: 600 }}>
                        Additional Information
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Autocomplete
                            fullWidth
                            options={brandOptions}
                            value={brand || null}
                            onChange={(_, val) => setBrand(val || '')}
                            disabled={loading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Brand"
                                    placeholder="Pick a brand"
                                    required
                                    helperText={isSingleComponent ? 'Auto-filled from Plytix — still editable, pick a different option if needed' : undefined}
                                />
                            )}
                        />
                        <Autocomplete
                            fullWidth
                            options={categoryOptions}
                            value={category || null}
                            onChange={(_, val) => setCategory(val || '')}
                            disabled={loading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Category"
                                    placeholder="Pick a category"
                                    required
                                    helperText={isSingleComponent ? 'Auto-filled from Plytix — still editable, pick a different option if needed' : undefined}
                                />
                            )}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            fullWidth
                            label="Sub Category"
                            placeholder="e.g., Festival"
                            value={subCategory}
                            onChange={(e) => setSubCategory(e.target.value)}
                            disabled={loading}
                            helperText={isSingleComponent ? 'Auto-filled from Plytix — still editable' : undefined}
                        />
                        <TextField
                            fullWidth
                            label="Handle"
                            placeholder="e.g., diwali-gift-box"
                            value={handle}
                            onChange={(e) => setHandle(e.target.value)}
                            disabled={loading}
                            helperText="URL-friendly identifier"
                        />
                    </Box>

                    {/* Tags Section */}
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, fontWeight: 600, mb: 1 }}>
                            Tags *
                        </Typography>
                        <Autocomplete
                            multiple
                            freeSolo
                            fullWidth
                            options={tagOptions}
                            value={tags}
                            onChange={(_, val) => setTags(val as string[])}
                            disabled={loading}
                            renderTags={(value, getTagProps) =>
                                value.map((tag, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={`${tag}-${index}`}
                                        label={tag}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={tags.length === 0 ? 'Select or type a tag and press Enter' : ''}
                                    size="small"
                                    required={tags.length === 0}
                                />
                            )}
                        />
                        {tags.length === 0 && (
                            <Typography variant="caption" color="text.secondary">
                                At least one tag is required
                            </Typography>
                        )}
                    </Box>

                    {/* Plytix ID Section */}
                    <TextField
                        fullWidth
                        label="Plytix ID (Optional)"
                        placeholder="Enter Plytix ID"
                        value={plytixId}
                        onChange={(e) => setPlytixId(e.target.value)}
                        disabled={loading}
                        helperText={isSingleComponent ? 'Auto-filled from Plytix — editable' : undefined}
                    />

                    {/* Pricing Section */}
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, fontWeight: 600 }}>
                        Pricing
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Max Retail Price (MRP)"
                            placeholder="e.g., 999.00"
                            value={maxRetailPrice}
                            onChange={(e) => setMaxRetailPrice(e.target.value)}
                            disabled={loading}
                            InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                            required
                            helperText={isSingleComponent ? 'Auto-filled from Plytix — editable' : 'Not auto-filled when multiple components are picked — no single Plytix record represents a combo price'}
                        />
                        <TextField
                            fullWidth
                            type="number"
                            label="Base Price"
                            placeholder="e.g., 799.00"
                            value={basePrice}
                            onChange={(e) => setBasePrice(e.target.value)}
                            disabled={loading}
                            InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                            required
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Cost Price"
                            placeholder="e.g., 362.88"
                            value={costPrice}
                            onChange={(e) => setCostPrice(e.target.value)}
                            disabled={loading}
                            InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                        />
                        <TextField
                            fullWidth
                            label="GST Tax Type Code"
                            placeholder="e.g., 3"
                            value={gstTaxTypeCode}
                            onChange={(e) => setGstTaxTypeCode(e.target.value)}
                            disabled={loading}
                        />
                    </Box>

                    {/* Image Section - at the end */}
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, fontWeight: 600 }}>
                        Image
                    </Typography>

                    <TextField
                        fullWidth
                        label="Image URL"
                        placeholder="https://example.com/bundle-image.jpg"
                        value={imageURL}
                        onChange={(e) => setImageURL(e.target.value)}
                        disabled={loading}
                        helperText={isSingleComponent ? 'Auto-filled from Plytix — editable' : 'Optional image for the bundle'}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || !parentSKU.trim() || selectedComponents.length === 0 || !brand.trim() || !category.trim() || tags.length === 0}
                    sx={{
                        background: 'linear-gradient(135deg, #DC5829 0%, #B8451E 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #b8451e 0%, #DC5829 100%)',
                        },
                    }}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Category'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateBundleCategory;
